import { useState, useRef, useCallback } from "react";

import { Form, Button, Modal } from "react-bootstrap";
import { Typography, Box, TextField, IconButton, Tooltip } from "@mui/material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import {
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  Image as ImageIcon,
  Link as LinkIcon,
  Code as CodeIcon,
  Title as TitleIcon,
  FormatQuote,
  TableChart,
} from "@mui/icons-material";

import { storage } from "../../../services/firebase";

const RichTextEditor = ({
  title,
  content,
  onTitleChange,
  onContentChange,
  onSave,
  showPreview = true,
  showToolbar = true,
}) => {
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [imageAlt, setImageAlt] = useState("");
  const textareaRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const insertAtCursor = useCallback(
    (text) => {
      const textarea = textareaRef.current?.querySelector("textarea");
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newContent = content.substring(0, start) + text + content.substring(end);

      onContentChange(newContent);

      // Set cursor position after inserted text
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + text.length, start + text.length);
      }, 0);
    },
    [content, onContentChange]
  );

  const wrapSelection = (before, after) => {
    const textarea = textareaRef.current?.querySelector("textarea");
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);
    const newContent =
      content.substring(0, start) + before + selectedText + after + content.substring(end);

    onContentChange(newContent);

    // Set cursor position after wrapped text
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + before.length,
        start + before.length + selectedText.length
      );
    }, 0);
  };

  const handleImageInsert = () => {
    if (imageUrl.trim()) {
      const imageMarkdown = `![${imageAlt || "Image"}](${imageUrl})`;
      insertAtCursor(imageMarkdown);
      setShowImageModal(false);
      setImageUrl("");
      setImageAlt("");
    }
  };

  const handleLinkInsert = () => {
    const url = prompt("Enter URL:");
    if (url) {
      const text = prompt("Enter link text (optional):") || url;
      const linkMarkdown = `[${text}](${url})`;
      insertAtCursor(linkMarkdown);
    }
  };

  // Update the drag handlers to prevent image drag default behavior
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    // Prevent browser from opening images
    if (
      e.dataTransfer.types.includes("Files") ||
      e.dataTransfer.types.includes("application/x-moz-file")
    ) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    // Explicitly show we can handle the drop
    e.dataTransfer.dropEffect = "copy";
  }, []);

  // Extract file upload logic to reuse for both drop and paste
  const handleFileUpload = useCallback(
    async (file) => {
      try {
        const storageRef = storage.ref();
        const fileRef = storageRef.child(`rich-text-images/${Date.now()}-${file.name}`);

        const uploadTask = fileRef.put(file);

        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(progress);
          },
          (error) => {
            console.error("Upload error:", error);
            // Show error to user
            alert("Failed to upload image: " + error.message);
            setUploadProgress(0);
          },
          async () => {
            try {
              const downloadURL = await fileRef.getDownloadURL();
              console.log("Upload complete, URL:", downloadURL); // Debug log
              const imageMarkdown = `![${file.name}](${downloadURL})`;
              insertAtCursor(imageMarkdown);
            } catch (error) {
              console.error("Error getting download URL:", error);
              alert("Failed to get image URL: " + error.message);
            } finally {
              setUploadProgress(0);
            }
          }
        );
      } catch (error) {
        console.error("Error initiating upload:", error);
        alert("Failed to start upload: " + error.message);
        setUploadProgress(0);
      }
    },
    [insertAtCursor, setUploadProgress]
  );

  // Add a paste handler for clipboard images
  const handlePaste = useCallback(
    (e) => {
      const items = Array.from(e.clipboardData?.items || []);
      const imageItems = items.filter((item) => item?.type?.startsWith("image/"));

      if (imageItems.length > 0) {
        e.preventDefault();

        imageItems.forEach((item) => {
          const file = item.getAsFile();
          if (file) {
            handleFileUpload(file);
          }
        });
      }
    },
    [handleFileUpload]
  );

  // Update handleDrop to use the new upload function
  const handleDrop = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files);
      const imageFiles = files.filter((file) => file?.type?.startsWith("image/"));

      for (const file of imageFiles) {
        await handleFileUpload(file);
      }
    },
    [handleFileUpload, setIsDragging]
  );

  const toolbarButtons = [
    {
      icon: <TitleIcon />,
      tooltip: "Heading",
      action: () => insertAtCursor("# Heading\n"),
    },
    {
      icon: <FormatBold />,
      tooltip: "Bold",
      action: () => wrapSelection("**", "**"),
    },
    {
      icon: <FormatItalic />,
      tooltip: "Italic",
      action: () => wrapSelection("*", "*"),
    },
    {
      icon: <FormatListBulleted />,
      tooltip: "Bullet List",
      action: () => insertAtCursor("- List item\n"),
    },
    {
      icon: <FormatListNumbered />,
      tooltip: "Numbered List",
      action: () => insertAtCursor("1. List item\n"),
    },
    {
      icon: <ImageIcon />,
      tooltip: "Insert Image",
      action: () => setShowImageModal(true),
    },
    {
      icon: <LinkIcon />,
      tooltip: "Insert Link",
      action: handleLinkInsert,
    },
    {
      icon: <CodeIcon />,
      tooltip: "Code Block",
      action: () => insertAtCursor("```\ncode here\n```\n"),
    },
    {
      icon: <FormatQuote />,
      tooltip: "Blockquote",
      action: () => insertAtCursor("> Quote text here\n"),
    },
    {
      icon: <TableChart />,
      tooltip: "Insert Table",
      action: () =>
        insertAtCursor(
          "| Header 1 | Header 2 | Header 3 |\n|----------|----------|----------|\n| Cell 1   | Cell 2   | Cell 3   |\n"
        ),
    },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      {/* Title Input */}
      <Form.Group className="mb-3">
        <Form.Label>Title</Form.Label>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          value={title || ""}
          onChange={(e) => onTitleChange(e.target.value)}
          placeholder="Enter the title for this info round"
        />
      </Form.Group>

      {/* Toolbar */}
      {showToolbar && (
        <Box
          sx={{
            border: "1px solid #ddd",
            borderBottom: "none",
            borderTopLeftRadius: "4px",
            borderTopRightRadius: "4px",
            padding: 1,
            backgroundColor: "#f8f9fa",
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
          }}
        >
          {toolbarButtons.map((button, index) => (
            <Tooltip key={index} title={button.tooltip}>
              <IconButton
                size="small"
                onClick={button.action}
                sx={{
                  "&:hover": { backgroundColor: "#e3f2fd" },
                  padding: 0.5,
                }}
              >
                {button.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>
      )}

      {/* Content Editor with drag & drop */}
      <Form.Group className="mb-3">
        <Form.Label>Content (Markdown)</Form.Label>
        <Box
          sx={{
            position: "relative",
            "&:hover": {
              "& .drag-overlay": {
                opacity: 0.1,
              },
            },
          }}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <TextField
            ref={textareaRef}
            fullWidth
            multiline
            rows={12}
            size="small"
            variant="outlined"
            value={content || ""}
            onChange={(e) => onContentChange(e.target.value)}
            onPaste={handlePaste} // Move comment outside of JSX
            placeholder="Enter your content here. Use the toolbar above to format your text, add images, links, and more. You can also drag and drop or paste images directly into the editor."
            sx={{
              "& .MuiOutlinedInput-root": {
                borderTopLeftRadius: showToolbar ? 0 : "4px",
                borderTopRightRadius: showToolbar ? 0 : "4px",
              },
            }}
          />

          {/* Drag & Drop Overlay */}
          {isDragging && (
            <Box
              className="drag-overlay"
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "primary.main",
                opacity: 0.2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "2px dashed",
                borderColor: "primary.main",
                borderRadius: 1,
                pointerEvents: "none",
              }}
            >
              <Typography variant="h6" color="primary.main">
                Drop images here
              </Typography>
            </Box>
          )}

          {/* Upload Progress */}
          {uploadProgress > 0 && (
            <Box
              sx={{
                position: "absolute",
                bottom: 8,
                right: 8,
                backgroundColor: "background.paper",
                padding: 1,
                borderRadius: 1,
                boxShadow: 1,
              }}
            >
              <Typography variant="body2">Uploading... {Math.round(uploadProgress)}%</Typography>
            </Box>
          )}
        </Box>
      </Form.Group>

      {/* Preview Toggle */}
      {showPreview && (
        <Box className="mb-3">
          <Typography variant="h6" gutterBottom>
            Preview
          </Typography>
          <Box
            sx={{
              minHeight: "200px",
              padding: 2,
              backgroundColor: "#fafafa",
              borderRadius: 1,
              border: "1px solid #e0e0e0",
              overflow: "auto",
            }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={{
                img: ({ node, ...props }) => (
                  <img
                    {...props}
                    style={{
                      maxWidth: "100%",
                      height: "auto",
                      borderRadius: "4px",
                      margin: "8px 0",
                    }}
                    alt={props.alt || "Image"}
                  />
                ),
                h1: ({ node, ...props }) => (
                  <Typography variant="h4" component="h1" gutterBottom {...props} />
                ),
                h2: ({ node, ...props }) => (
                  <Typography variant="h5" component="h2" gutterBottom {...props} />
                ),
                h3: ({ node, ...props }) => (
                  <Typography variant="h6" component="h3" gutterBottom {...props} />
                ),
                p: ({ node, ...props }) => <Typography variant="body1" paragraph {...props} />,
                ul: ({ node, ...props }) => <Box component="ul" sx={{ pl: 2, mb: 1 }} {...props} />,
                ol: ({ node, ...props }) => <Box component="ol" sx={{ pl: 2, mb: 1 }} {...props} />,
                a: ({ node, ...props }) => (
                  <Typography
                    component="a"
                    color="primary"
                    sx={{ textDecoration: "underline" }}
                    {...props}
                  />
                ),
                code: ({ node, inline, ...props }) => {
                  if (inline) {
                    return (
                      <Box
                        component="code"
                        sx={{
                          backgroundColor: "#f5f5f5",
                          padding: "1px 3px",
                          borderRadius: "3px",
                          fontFamily: "monospace",
                          fontSize: "0.9em",
                        }}
                        {...props}
                      />
                    );
                  }
                  return (
                    <Box
                      component="pre"
                      sx={{
                        backgroundColor: "#f5f5f5",
                        padding: 1,
                        borderRadius: 1,
                        overflow: "auto",
                        margin: "8px 0",
                      }}
                    >
                      <Box
                        component="code"
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.9em",
                        }}
                        {...props}
                      />
                    </Box>
                  );
                },
                blockquote: ({ node, ...props }) => (
                  <Box
                    component="blockquote"
                    sx={{
                      borderLeft: "3px solid #1976d2",
                      paddingLeft: 1,
                      margin: "8px 0",
                      fontStyle: "italic",
                      backgroundColor: "#f8f9fa",
                      padding: 1,
                      borderRadius: "0 3px 3px 0",
                    }}
                    {...props}
                  />
                ),
                table: ({ node, ...props }) => (
                  <Box
                    component="table"
                    sx={{
                      width: "100%",
                      borderCollapse: "collapse",
                      margin: "8px 0",
                      "& th, & td": {
                        border: "1px solid #ddd",
                        padding: "4px 8px",
                        textAlign: "left",
                        fontSize: "0.9em",
                      },
                      "& th": {
                        backgroundColor: "#f5f5f5",
                        fontWeight: "bold",
                      },
                    }}
                    {...props}
                  />
                ),
              }}
            >
              {content || "No content to preview"}
            </ReactMarkdown>
          </Box>
        </Box>
      )}

      {/* Image Insert Modal */}
      <Modal show={showImageModal} onHide={() => setShowImageModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Insert Image</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Image URL</Form.Label>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://example.com/image.jpg"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Alt Text (optional)</Form.Label>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              value={imageAlt}
              onChange={(e) => setImageAlt(e.target.value)}
              placeholder="Description of the image"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImageModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleImageInsert}>
            Insert Image
          </Button>
        </Modal.Footer>
      </Modal>
    </Box>
  );
};

export default RichTextEditor;
